/**
 * useRingtone — synthesized Messenger/WhatsApp-style ring sounds.
 *
 * We deliberately DON'T ship an MP3: copyright-safe, zero download,
 * and identical behaviour on every device. Web Audio API renders two
 * short cheerful bells for the callee (Messenger vibe) and a warm
 * two-pulse "brrr-brrr" for the caller (WhatsApp ringback).
 *
 * Loops until `stop()` is called. Volume auto-drops if the tab loses
 * focus. iOS Safari needs a user-gesture-initiated AudioContext, so
 * we only call `resume()` inside event handlers.
 */
import { useEffect, useRef } from "react";

// ---- Bell for incoming (Messenger "ding-ding") ----
function playBell(ctx, when, freqA, freqB) {
  const now = when ?? ctx.currentTime;
  const dur = 0.55;
  // Two overlapping sine waves for a fuller tone.
  [
    { freq: freqA, gain: 0.35 },
    { freq: freqB, gain: 0.22 },
    { freq: freqA * 2, gain: 0.08 }, // subtle harmonic
  ].forEach(({ freq, gain }) => {
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, now);
    env.gain.setValueAtTime(0, now);
    env.gain.linearRampToValueAtTime(gain, now + 0.02);
    env.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(env).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + dur + 0.05);
  });
}

// ---- Ringback for caller (WhatsApp "brrr-brrr") ----
function playRingback(ctx, when) {
  const now = when ?? ctx.currentTime;
  const dur = 0.8;
  const osc1 = ctx.createOscillator();
  const osc2 = ctx.createOscillator();
  const env = ctx.createGain();
  osc1.type = "sine";
  osc2.type = "sine";
  // Warm two-note beat, like classic phone ring.
  osc1.frequency.setValueAtTime(440, now);
  osc2.frequency.setValueAtTime(480, now);
  env.gain.setValueAtTime(0, now);
  env.gain.linearRampToValueAtTime(0.18, now + 0.03);
  env.gain.linearRampToValueAtTime(0.18, now + dur - 0.05);
  env.gain.exponentialRampToValueAtTime(0.0001, now + dur);
  osc1.connect(env);
  osc2.connect(env);
  env.connect(ctx.destination);
  osc1.start(now);
  osc2.start(now);
  osc1.stop(now + dur);
  osc2.stop(now + dur);
}

/**
 * @param {"incoming"|"ringback"|null} kind
 *   - "incoming":  callee-side Messenger-style "ding-ding", every 2 s
 *   - "ringback":  caller-side WhatsApp-style "brrr brrr", every 3 s
 *   - null:        silent
 */
export function useRingtone(kind) {
  const ctxRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!kind) return undefined;
    let stopped = false;

    async function start() {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        // iOS: context starts suspended until a gesture resumes it.
        try { await ctx.resume(); } catch { /* ignore */ }
        ctxRef.current = ctx;
        const tick = () => {
          if (stopped) return;
          try {
            if (kind === "incoming") {
              // Two ascending bells 200 ms apart — Messenger vibe.
              playBell(ctx, ctx.currentTime, 880, 1108); // A5, C#6
              playBell(ctx, ctx.currentTime + 0.22, 1108, 1318); // C#6, E6
            } else if (kind === "ringback") {
              playRingback(ctx, ctx.currentTime);
              playRingback(ctx, ctx.currentTime + 1.0);
            }
          } catch { /* ignore */ }
        };
        tick();
        const period = kind === "incoming" ? 2000 : 3200;
        timerRef.current = setInterval(tick, period);
      } catch { /* ignore */ }
    }

    start();

    return () => {
      stopped = true;
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      try {
        ctxRef.current?.close();
      } catch { /* ignore */ }
      ctxRef.current = null;
    };
  }, [kind]);
}
